import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import {
  HR_COMPENSATION_PACKAGE_STATUSES,
  HR_EMPLOYEE_TYPES,
  HR_PAY_CALCULATION_TYPES,
  HR_PAY_ELEMENT_TYPES,
  HR_PAYROLL_PERIOD_STATUSES,
  HrCompensationPackage,
  HrCompensationPackageElement,
  HrDepartment,
  HrEmployee,
  HrPayElement,
  HrPayrollAccountingBatch,
  HrPayrollAccountingMapping,
  HrPayrollAccountingPreview,
  HrPayrollDashboard,
  HrPayrollException,
  HrPayrollInput,
  HrPayrollPeriod,
  HrPayrollPaymentBatch,
  HrPayrollRun,
  HrPayrollRunLine,
  HrPayslip
} from '../hr.models';
import { HrService } from '../hr.service';
import { HrActionDialogComponent } from '../shared/hr-action-dialog.component';
import { HrListTabsComponent } from '../shared/hr-list-tabs.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

@Component({
  selector: 'app-hr-payroll',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    PageFormSectionComponent,
    HrActionDialogComponent,
    HrListTabsComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-payroll.component.html',
  styleUrls: ['./hr-payroll.component.scss']
})
export class HrPayrollComponent implements OnInit {
  activeListTab = 'runs';
  private readonly hrService = inject(HrService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  readonly canManagePayroll = this.permissionService.hasPermission(app_permissions.hr.payroll.manage);
  readonly canCalculatePayroll = this.permissionService.hasPermission(app_permissions.hr.payroll.calculate);
  readonly canApprovePayroll = this.permissionService.hasPermission(app_permissions.hr.payroll.approve);
  readonly canViewPayrollAccounting = this.permissionService.hasPermission(app_permissions.hr.payroll.accountingView);
  readonly canManagePayrollAccounting = this.permissionService.hasPermission(app_permissions.hr.payroll.accountingManage);
  readonly canPostPayrollAccounting = this.permissionService.hasPermission(app_permissions.hr.payroll.accountingPost);
  readonly canManagePayrollPayment = this.permissionService.hasPermission(app_permissions.hr.payroll.paymentManage);
  readonly payElementTypes = HR_PAY_ELEMENT_TYPES;
  readonly calculationTypes = HR_PAY_CALCULATION_TYPES;
  readonly reportingCategories = [
    { label: 'Not classified', value: null },
    { label: 'Regular pay', value: 'REGULAR_PAY' },
    { label: 'Overtime', value: 'OVERTIME' },
    { label: 'Allowance', value: 'ALLOWANCE' },
    { label: 'Bonus', value: 'BONUS' },
    { label: 'Tax', value: 'TAX' },
    { label: 'Social insurance', value: 'SOCIAL_INSURANCE' },
    { label: 'Other reportable cost', value: 'OTHER' }
  ];
  readonly packageStatuses = HR_COMPENSATION_PACKAGE_STATUSES;
  readonly periodStatuses = HR_PAYROLL_PERIOD_STATUSES;
  readonly employeeTypeOptions = HR_EMPLOYEE_TYPES.map(value => ({ label: this.format(value), value }));
  workerCategoryOptions: { label: string; value: string }[] = [];

  dashboard?: HrPayrollDashboard;
  employees: HrEmployee[] = [];
  departments: HrDepartment[] = [];
  payElements: HrPayElement[] = [];
  packages: HrCompensationPackage[] = [];
  periods: HrPayrollPeriod[] = [];
  runs: HrPayrollRun[] = [];
  runLines: HrPayrollRunLine[] = [];
  exceptions: HrPayrollException[] = [];
  payslips: HrPayslip[] = [];
  accountingMappings: HrPayrollAccountingMapping[] = [];
  accountingBatches: HrPayrollAccountingBatch[] = [];
  paymentBatches: HrPayrollPaymentBatch[] = [];
  postingPreview?: HrPayrollAccountingPreview;
  selectedRun?: HrPayrollRun;
  selectedLine?: HrPayrollRunLine;
  selectedPayslip?: HrPayslip;
  selectedPackageId?: number;
  lineDetailVisible = false;
  payslipVisible = false;
  activeDialog?: 'element' | 'period' | 'package' | 'packageElement' | 'run' | 'input' | 'mapping';

  payElementForm: HrPayElement = this.newPayElement();
  periodForm: HrPayrollPeriod = this.newPeriod();
  packageForm: HrCompensationPackage = this.newPackage();
  packageElementForm: HrCompensationPackageElement = this.newPackageElement();
  inputForm: HrPayrollInput = this.newInput();
  mappingForm: HrPayrollAccountingMapping = this.newMapping();
  runForm = { payrollPeriodId: undefined as number | undefined, runCode: '', runName: '', employeeId: undefined as number | undefined };

  ngOnInit(): void {
    this.loadWorkerCategories();
    this.loadAll();
  }

  private loadWorkerCategories(): void {
    this.hrService.workerCategories().subscribe(response => {
      this.workerCategoryOptions = (response.data ?? []).map(item => ({
        label: item.categoryName || this.format(item.categoryCode), value: item.categoryCode || ''
      }));
    });
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Pay Elements', value: this.dashboard?.activePayElements || 0 },
      { label: 'Comp Packages', value: this.dashboard?.activeCompensationPackages || 0 },
      { label: 'Open Periods', value: this.dashboard?.openPeriods || 0, tone: 'info' },
      { label: 'Draft Runs', value: this.dashboard?.draftRuns || 0, tone: 'warn' },
      { label: 'Open Exceptions', value: this.dashboard?.openExceptions || 0, tone: (this.dashboard?.openExceptions || 0) > 0 ? 'danger' : 'success' },
      { label: 'Payslips', value: this.dashboard?.generatedPayslips || 0 }
    ];
  }

  get employeeOptions() {
    return this.employees.map(employee => ({
      label: `${employee.displayName || employee.employeeNumber || employee.employeeId} (${employee.employeeNumber || employee.employeeId})`,
      value: employee.employeeId
    }));
  }

  get departmentOptions() {
    return this.departments.map(department => ({
      label: `${department.departmentName || department.departmentCode || 'Department'} (${department.departmentCode || department.departmentId || '-'})`,
      value: department.departmentId
    }));
  }

  get payElementOptions() {
    return this.payElements.map(element => ({ label: `${element.elementName} (${element.elementCode})`, value: element.payElementId }));
  }

  get packageOptions() {
    return this.packages.map(pkg => ({ label: `${pkg.packageName} - ${pkg.employeeName}`, value: pkg.compensationPackageId }));
  }

  get periodOptions() {
    return this.periods.map(period => ({ label: `${period.periodName} (${period.periodCode})`, value: period.payrollPeriodId }));
  }

  loadAll(): void {
    this.hrService.payrollDashboard().subscribe(response => {
      this.dashboard = response.data;
      this.exceptions = response.data?.exceptions || [];
    });
    this.hrService.employees({ page: 0, pageSize: 300 })
      .subscribe(response => this.employees = response.items || []);
    this.hrService.departments().subscribe(response => this.departments = response.data || []);
    this.hrService.payElements(undefined, true).subscribe(response => this.payElements = response.data || []);
    this.hrService.compensationPackages().subscribe(response => this.packages = response.data || []);
    this.hrService.payrollPeriods(undefined, undefined, 100).subscribe(response => this.periods = response.data || []);
    this.hrService.payrollRuns(undefined, undefined, 50).subscribe(response => this.runs = response.data || []);
    this.hrService.payrollAccountingMappings(undefined, true).subscribe(response => this.accountingMappings = response.data || []);
    this.hrService.payrollAccountingBatches(undefined, undefined, 20).subscribe(response => this.accountingBatches = response.data || []);
    this.hrService.payrollPaymentBatches(undefined, undefined, 20).subscribe(response => this.paymentBatches = response.data || []);
  }

  savePayElement(): void {
    this.hrService.savePayElement(this.payElementForm).subscribe({
      next: () => {
        this.toast('success', 'Pay element saved.');
        this.payElementForm = this.newPayElement();
        this.activeDialog = undefined;
        this.loadAll();
      },
      error: () => this.toast('error', 'Could not save pay element.')
    });
  }

  savePeriod(): void {
    this.hrService.savePayrollPeriod(this.periodForm).subscribe({
      next: response => {
        this.toast('success', 'Payroll period saved.');
        this.periodForm = this.newPeriod();
        this.runForm.payrollPeriodId = response.data?.payrollPeriodId;
        this.activeDialog = undefined;
        this.loadAll();
      },
      error: () => this.toast('error', 'Could not save payroll period.')
    });
  }

  savePackage(): void {
    this.hrService.saveCompensationPackage(this.packageForm).subscribe({
      next: response => {
        this.toast('success', 'Compensation package saved.');
        this.packageForm = this.newPackage();
        this.selectedPackageId = response.data?.compensationPackageId;
        this.activeDialog = undefined;
        this.loadAll();
      },
      error: () => this.toast('error', 'Could not save compensation package.')
    });
  }

  savePackageElement(): void {
    if (!this.selectedPackageId) {
      return;
    }
    this.hrService.saveCompensationPackageElement(this.selectedPackageId, this.packageElementForm).subscribe({
      next: () => {
        this.toast('success', 'Package element saved.');
        this.packageElementForm = this.newPackageElement();
        this.activeDialog = undefined;
        this.loadAll();
      },
      error: () => this.toast('error', 'Could not save package element.')
    });
  }

  saveInput(): void {
    this.hrService.savePayrollInput(this.inputForm).subscribe({
      next: () => {
        this.toast('success', 'Payroll input saved.');
        this.inputForm = this.newInput();
        this.activeDialog = undefined;
      },
      error: () => this.toast('error', 'Could not save payroll input.')
    });
  }

  calculateRun(): void {
    this.hrService.calculatePayrollRun({
      payrollPeriodId: this.runForm.payrollPeriodId,
      runCode: this.runForm.runCode,
      runName: this.runForm.runName,
      employeeId: this.runForm.employeeId,
      regeneratePayslips: true
    }).subscribe({
      next: response => {
        this.toast('success', 'Payroll run calculated.');
        this.selectedRun = response.data;
        this.runLines = response.data?.lines || [];
        this.exceptions = response.data?.exceptions || [];
        this.loadPayslips(response.data?.payrollRunId);
        this.activeDialog = undefined;
        this.loadAll();
      },
      error: () => this.toast('error', 'Could not calculate payroll run.')
    });
  }

  selectRun(run: HrPayrollRun): void {
    if (!run.payrollRunId) {
      return;
    }
    this.hrService.payrollRun(run.payrollRunId).subscribe(response => {
      this.selectedRun = response.data;
      this.runLines = response.data?.lines || [];
      this.exceptions = response.data?.exceptions || [];
      this.loadPayslips(run.payrollRunId);
    });
  }

  generatePayslips(run: HrPayrollRun): void {
    if (!run.payrollRunId) {
      return;
    }
    this.hrService.generatePayrollPayslips(run.payrollRunId).subscribe({
      next: response => {
        this.payslips = response.data || [];
        this.toast('success', 'Payslips generated.');
      },
      error: () => this.toast('error', 'Could not generate payslips.')
    });
  }

  approveRun(run: HrPayrollRun): void {
    if (!run.payrollRunId) {
      return;
    }
    this.hrService.approvePayrollRun(run.payrollRunId).subscribe({
      next: response => {
        this.toast('success', 'Payroll run approved.');
        this.selectedRun = response.data;
        this.loadAll();
      },
      error: () => this.toast('error', 'Could not approve payroll run. Resolve blocker exceptions first.')
    });
  }

  saveAccountingMapping(): void {
    this.hrService.savePayrollAccountingMapping(this.mappingForm).subscribe({
      next: () => {
        this.toast('success', 'Payroll accounting mapping saved.');
        this.mappingForm = this.newMapping();
        this.activeDialog = undefined;
        this.loadAll();
      },
      error: () => this.toast('error', 'Could not save accounting mapping.')
    });
  }

  editAccountingMapping(mapping: HrPayrollAccountingMapping): void {
    this.mappingForm = { ...mapping };
    this.activeDialog = 'mapping';
  }

  openActionDialog(dialog: NonNullable<HrPayrollComponent['activeDialog']>): void {
    this.activeDialog = dialog;
  }

  actionDialogVisible(dialog: NonNullable<HrPayrollComponent['activeDialog']>): boolean {
    return this.activeDialog === dialog;
  }

  setActionDialogVisible(dialog: NonNullable<HrPayrollComponent['activeDialog']>, visible: boolean): void {
    this.activeDialog = visible ? dialog : undefined;
  }

  loadPostingPreview(run: HrPayrollRun): void {
    if (!run.payrollRunId) {
      return;
    }
    this.hrService.payrollPostingPreview(run.payrollRunId).subscribe({
      next: response => {
        this.selectedRun = run;
        this.postingPreview = response.data;
      },
      error: () => this.toast('error', 'Could not load posting preview.')
    });
  }

  postAccounting(run: HrPayrollRun): void {
    if (!run.payrollRunId) {
      return;
    }
    this.hrService.postPayrollAccounting(run.payrollRunId, undefined, 'Posted from HR payroll workspace.').subscribe({
      next: response => {
        this.toast('success', 'Payroll accounting handoff posted.');
        this.accountingBatches = response.data ? [response.data, ...this.accountingBatches] : this.accountingBatches;
        this.loadAll();
      },
      error: () => this.toast('error', 'Could not post payroll accounting. Check approval, locks, and mappings.')
    });
  }

  createPaymentProposal(run: HrPayrollRun): void {
    if (!run.payrollRunId) {
      return;
    }
    this.hrService.createPayrollPaymentProposal(run.payrollRunId, undefined, 'Payment proposal from HR payroll workspace.').subscribe({
      next: response => {
        this.toast('success', 'Payroll payment proposal created.');
        this.paymentBatches = response.data ? [response.data, ...this.paymentBatches] : this.paymentBatches;
        this.loadAll();
      },
      error: () => this.toast('error', 'Could not create payment proposal. Post accounting first.')
    });
  }

  reverseAccounting(run: HrPayrollRun): void {
    if (!run.payrollRunId) {
      return;
    }
    this.hrService.reversePayrollAccounting(run.payrollRunId, undefined, 'Reversed from HR payroll workspace.').subscribe({
      next: () => {
        this.toast('success', 'Payroll accounting handoff reversed.');
        this.loadAll();
      },
      error: () => this.toast('error', 'Could not reverse payroll accounting.')
    });
  }

  openLineDetail(line: HrPayrollRunLine): void {
    this.selectedLine = line;
    this.lineDetailVisible = true;
  }

  openPayslip(slip: HrPayslip): void {
    this.selectedPayslip = slip;
    this.payslipVisible = true;
  }

  downloadPayslip(slip: HrPayslip): void {
    const payload = this.prettyPayslip(slip);
    const blob = new Blob([payload], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${slip.payslipNumber || 'payslip'}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  prettyPayslip(slip: HrPayslip): string {
    if (slip.payslipDataJson) {
      try {
        return JSON.stringify(JSON.parse(slip.payslipDataJson), null, 2);
      } catch {
        return slip.payslipDataJson;
      }
    }
    return JSON.stringify(slip, null, 2);
  }

  money(value?: number, currency = 'SAR'): string {
    return `${currency || 'SAR'} ${(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  format(value?: string): string {
    return (value || '-').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
  }

  runSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'APPROVED' || status === 'CALCULATED') return 'success';
    if (status === 'CALCULATED_WITH_EXCEPTIONS') return 'warn';
    if (status === 'CALCULATING') return 'info';
    return 'secondary';
  }

  private loadPayslips(payrollRunId?: number): void {
    this.hrService.payrollPayslips(undefined, payrollRunId).subscribe(response => this.payslips = response.data || []);
  }

  private newPayElement(): HrPayElement {
    return { elementType: 'EARNING', calculationType: 'FIXED', defaultCurrencyCode: 'SAR', active: true, taxable: true, affectsGross: true, affectsNet: true };
  }

  private newPeriod(): HrPayrollPeriod {
    return { periodStatus: 'OPEN', currencyCode: 'SAR' };
  }

  private newPackage(): HrCompensationPackage {
    return { packageStatus: 'DRAFT', currencyCode: 'SAR', payrollHold: false, bankAccountMissing: false };
  }

  private newPackageElement(): HrCompensationPackageElement {
    return { amount: 0, quantity: 1, frequency: 'MONTHLY', active: true };
  }

  private newInput(): HrPayrollInput {
    return { sourceType: 'MANUAL', inputStatus: 'READY', amount: 0, quantity: 1, currencyCode: 'SAR' };
  }

  private newMapping(): HrPayrollAccountingMapping {
    return {
      payableType: 'SALARY_PAYABLE',
      mappingPriority: 100,
      active: true
    };
  }

  private toast(severity: 'success' | 'error' | 'info', detail: string): void {
    this.messageService.add({ severity, summary: severity === 'error' ? 'Payroll' : 'Saved', detail });
  }
}
